import { forwardRef, type InputHTMLAttributes, type Ref } from 'react';
import { type FieldValues, type Path, type FieldErrors } from 'react-hook-form';
import { type FormGroupProps } from '../FormGroup/FormGroup';

import {
  StyledFormGroup,
  StyledErrorIcon,
  StyledInput,
  StyledInputContainer,
} from './Input.styles';

interface InputProps<T extends FieldValues> extends InputHTMLAttributes<HTMLInputElement> {
  name: Path<T>;
  id: string;
  moveToTop?: boolean;
  formGroupProps: Omit<FormGroupProps, 'name' | 'htmlFor' | 'required' | 'children'>;
  errors?: FieldErrors<T>;
}

const Input = forwardRef(
  <T extends FieldValues>(
    { name, id, required, formGroupProps, errors, className, moveToTop, ...rest }: InputProps<T>,
    ref?: Ref<HTMLInputElement>
  ) => {
    const hasError = Boolean(errors?.[name]?.message);

    return (
      <StyledFormGroup
        moveToTop={moveToTop}
        {...formGroupProps}
        htmlFor={id}
        required={required}
        name={name}
        errors={errors}
        className={className}
      >
        <StyledInputContainer>
          <StyledInput
            hasError={hasError}
            id={id}
            required={required}
            name={name}
            {...rest}
            ref={ref}
          />
          {hasError && <StyledErrorIcon name="warning-circle" />}
        </StyledInputContainer>
      </StyledFormGroup>
    );
  }
);

export default Input;
